import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getSupabaseBrowser } from '../supabaseClient';

// Duplicated inline from MapScreen.js — do not extract to a shared module.
function renderItineraryLines(text, compact = false) {
  return text.split('\n').map((line, i) => {
    if (!line.trim()) return <div key={i} style={{ height: compact ? 6 : 10 }} />;
    if (line === '---') return <div key={i} style={{ height: 1, background: '#2a2825', margin: compact ? '16px 0' : '24px 0' }} />;
    if (line === 'Safe travels. You know this city now.') {
      return (
        <div key={i} style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: compact ? 14 : 18, fontStyle: 'italic',
          color: '#c8a96e', marginTop: compact ? 20 : 32, textAlign: 'center', lineHeight: 1.4,
        }}>
          {line}
        </div>
      );
    }
    if (/^DAY \d+/.test(line)) {
      return (
        <div key={i} style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: compact ? 16 : 22, fontWeight: 700,
          color: '#f0ebe3', marginTop: compact ? 22 : 36, marginBottom: 3, lineHeight: 1.2,
        }}>
          {line}
        </div>
      );
    }
    if (/^\d+:\d+/.test(line) || /^(Morning|Afternoon|Evening|Night) —/.test(line)) {
      return (
        <div key={i} style={{
          fontSize: compact ? 11 : 13, fontWeight: 600, color: '#c8a96e',
          marginTop: compact ? 12 : 18, marginBottom: 2, letterSpacing: '0.02em',
        }}>
          {line}
        </div>
      );
    }
    return (
      <p key={i} style={{ fontSize: compact ? 12 : 13, lineHeight: 1.75, color: '#8a8278', margin: '0 0 2px' }}>
        {line}
      </p>
    );
  });
}

export default function ItineraryView() {
  const { id } = useParams();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [row, setRow] = useState(null);

  useEffect(() => {
    if (!id) { setStatus('error'); return; }
    const supabase = getSupabaseBrowser();
    if (!supabase) { setStatus('error'); return; }

    supabase
      .from('itineraries')
      .select('*')
      .eq('id', id)
      .single()
      .then(({ data, error }) => {
        if (error || !data) {
          setStatus('error');
        } else {
          setRow(data);
          setStatus('success');
        }
      });
  }, [id]);

  const baseStyle = {
    fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
    background: '#000000',
    minHeight: '100vh',
    color: '#f0ebe3',
  };

  if (status === 'loading') {
    return (
      <div style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,400;1,700&family=DM+Sans:wght@300;400;500&display=swap');`}</style>
        <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontSize: 22, color: '#f0ebe3' }}>
          Loading your itinerary...
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ ...baseStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,400;1,700&family=DM+Sans:wght@300;400;500&display=swap');`}</style>
        <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontSize: 20, color: '#f0ebe3', textAlign: 'center', maxWidth: 480, padding: '0 24px' }}>
          This itinerary couldn't be found. It may have expired.
        </div>
        <a href="/" style={{ color: '#8a8278', fontSize: 13, textDecoration: 'none', marginTop: 8 }}>
          ← Back to CityMirror
        </a>
      </div>
    );
  }

  const text = row?.itinerary_data?.text ?? '';

  return (
    <div style={baseStyle}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,400;1,700&family=DM+Sans:wght@300;400;500&display=swap');`}</style>

      {/* Header */}
      <div style={{ borderBottom: '1px solid #1a1815', padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <a href="/" style={{ color: '#c8a96e', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', textDecoration: 'none' }}>
          CityMirror
        </a>
        <div style={{ fontSize: 11, color: '#524e49', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
          Your Itinerary
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '40px 28px 80px', width: '100%' }}>
        {renderItineraryLines(text, false)}
      </div>
    </div>
  );
}
